import { useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Logo } from '@/components/Logo'
import { MagneticButton } from '@/components/MagneticButton'
import { SiteFooter } from '@/components/ui/hero-sections'
import { useSmoothScroll } from '@/lib/useSmoothScroll'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'
import { useAuth, gatedTarget, loginUrl, displayName, firstName, initials, type User } from '@/lib/auth'
import { cn } from '@/lib/utils'

/* ================================================================== */
/*  Site chrome: the one nav used everywhere (simple menu, no          */
/*  breadcrumbs) and a shared dark page shell for standalone pages.    */
/* ================================================================== */

const NAV_LINKS = [
  { to: '/communities', label: 'Communities' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/lifecrafter-program', label: 'LifeCrafter Program' },
]

/** Initials avatar. No picture comes back from /auth/me, so we derive one. */
function Avatar({ user, size = 34 }: { user: User | null; size?: number }) {
  return (
    <span
      className="grid shrink-0 place-items-center rounded-full bg-lime font-bold text-night"
      style={{ height: size, width: size, fontSize: Math.round(size * 0.4) }}
      aria-hidden="true"
    >
      {initials(user)}
    </span>
  )
}

/**
 * Passive "you're signed in" badge. LifeCraft owns no account UI — login,
 * logout and profile all live in the webapp. This only mirrors the shared
 * session: if the user logs out there, the next page load here reads /auth/me
 * again and this reverts to the guest "Log in" link.
 */
function SignedInBadge({ user }: { user: User | null }) {
  return (
    <div className="lc-glass-soft hidden items-center gap-2 rounded-pill py-1.5 pl-1.5 pr-4 text-lc-ink md:flex">
      <Avatar user={user} size={30} />
      <span className="label max-w-[7rem] truncate">{firstName(user)}</span>
    </div>
  )
}

export function SiteNav() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const { user, authed, loading, logout } = useAuth()
  // Logged in → explore communities; logged out → join funnel (signup).
  const joinTo = gatedTarget(authed, '/communities', '/signup')
  // Sign out clears the shared session cookie (via /auth/logout) so the user is
  // logged out of the webapp too, then drops local state. Falls back to just
  // clearing local state if the network call can't reach the API.
  const signOut = () => { void logout() }

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-5 pt-4 md:px-0">
      <div className="container-lc">
        <nav className="lc-navbar flex h-16 items-center justify-between rounded-panel pl-5 pr-3">
          <Link to="/" aria-label="LifeCraft4U home" onClick={() => setOpen(false)}>
            <Logo size={27} className="nav-logo" wordmarkClass="hidden sm:inline-flex" />
          </Link>

          <div className="hidden items-center gap-7 min-[1100px]:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  cn('label transition-colors', isActive ? 'text-lc-ink' : 'text-lc-mute hover:text-lc-ink')
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-4">
            {loading ? (
              <span className="hidden h-[42px] w-24 animate-pulse rounded-pill bg-lc-line md:block" aria-hidden="true" />
            ) : authed ? (
              <div className="hidden items-center gap-3 md:flex">
                <SignedInBadge user={user} />
                <button onClick={signOut} className="label text-lc-mute transition-colors hover:text-lc-ink">Log out</button>
              </div>
            ) : (
              <a href={loginUrl()} className="label hidden text-lc-mute transition-colors hover:text-lc-ink md:block">Log in</a>
            )}
            <MagneticButton href={joinTo} data-cursor="join" className="hidden rounded-pill bg-lime px-5 py-2.5 text-[15px] font-bold text-night min-[1100px]:inline-flex">
              {authed ? 'Explore communities' : 'Join a community'}
            </MagneticButton>
            <button
              onClick={() => setOpen(!open)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              className="lc-glass-soft grid h-11 w-11 place-items-center rounded-pill text-lc-ink min-[1100px]:hidden"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                {open ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
              </svg>
            </button>
          </div>
        </nav>

        {/* mobile menu */}
        {open && (
          <div className="lc-menu mt-2 rounded-panel p-2 min-[1100px]:hidden">
            {authed && (
              <div className="mb-1 flex items-center gap-3 rounded-tile px-3 py-3 md:hidden">
                <Avatar user={user} size={38} />
                <p className="truncate text-[15px] xl:text-[16px] font-semibold text-lc-ink">{displayName(user)}</p>
              </div>
            )}
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={cn(
                  'block rounded-tile px-4 py-3 text-[15px] xl:text-[16px] font-semibold transition-colors',
                  pathname.startsWith(l.to) ? 'text-lc-ink' : 'text-lc-mute hover:bg-lc-line hover:text-lc-ink',
                )}
              >
                {l.label}
              </Link>
            ))}
            {!authed && (
              <a
                href={loginUrl()}
                onClick={() => setOpen(false)}
                className="mt-1 block rounded-tile px-4 py-3 text-[15px] xl:text-[16px] font-semibold text-lc-mute transition-colors hover:bg-lc-line hover:text-lc-ink md:hidden"
              >
                Log in
              </a>
            )}
            <Link
              to={joinTo}
              onClick={() => setOpen(false)}
              className="mt-2 block rounded-pill bg-lime px-4 py-3.5 text-center text-[15px] xl:text-[16px] font-bold text-night"
            >
              {authed ? 'Explore communities' : 'Join a community'}
            </Link>
            {authed && (
              <button
                onClick={() => { setOpen(false); signOut() }}
                className="mt-1 block w-full rounded-tile px-4 py-3 text-left text-[15px] xl:text-[16px] font-semibold text-lc-mute transition-colors hover:bg-lc-line hover:text-lc-ink"
              >
                Log out
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  )
}

/* ---- shared dark shell for standalone pages ------------------------- */
function ShellBackdrop() {
  const reduced = usePrefersReducedMotion()
  const blobs = [
    { cls: 'h-[50rem] w-[50rem] -left-56 top-24 opacity-40', color: '#7C3AED', anim: { x: [0, 80, 0], y: [0, 50, 0] }, d: 26 },
    { cls: 'h-[42rem] w-[42rem] -right-40 top-[22%] opacity-35', color: '#FF6B5C', anim: { x: [0, -70, 0], y: [0, 70, 0] }, d: 33 },
    { cls: 'h-[46rem] w-[46rem] left-[22%] top-[60%] opacity-35', color: '#5B21B6', anim: { x: [0, 60, 0], y: [0, -60, 0] }, d: 39 },
  ]
  return (
    <div className="grain grain-on-dark pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #191231 0%, #130E22 55%, #170F2A 100%)' }} />
      {blobs.map((b, i) => (
        <motion.div
          key={i}
          className={cn('blob', b.cls)}
          style={{ background: b.color }}
          animate={reduced ? undefined : b.anim}
          transition={reduced ? undefined : { duration: b.d, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 38%, transparent 42%, rgba(8,6,16,0.72) 100%)' }} />
      {/* phones: the blurred blobs bloom brighter on a small canvas, washing the
          bg pink and killing text contrast — lay a darker scrim on mobile only */}
      <div className="absolute inset-0 md:hidden" style={{ background: 'rgba(9,6,18,0.5)' }} />
    </div>
  )
}

export function PageShell({ children }: { children: React.ReactNode }) {
  useSmoothScroll()
  return (
    <div className="lc-dark relative min-h-screen overflow-x-clip bg-night-800 text-lc-ink">
      <ShellBackdrop />
      <SiteNav />
      <main className="relative z-10">{children}</main>
      <SiteFooter dark />
    </div>
  )
}
